import api from '../../../services/api/';

const URL = '/inventory_items';

class InventoryItemService {
  // Get list of inventory items with filters
  getList(params = '') {
    return api.get(`${URL}${params.length ? `?${params}` : ''}`);
  }


  getItem(id) {
    return api.get(`${URL}/${id}`);
  }
  // Get single inventory item by ID
  getById(id) {
    return api.get(`${URL}/${id}`);
  }

  // Create new inventory item
  create(data) {
    return api.post(URL, data);
  }

  // Update existing inventory item
  update(id, data) {
    return api.put(`${URL}/${id}`, data);
  }

  // Delete inventory item
  delete(id) {
    return api.delete(`${URL}/${id}`);
  }

  // Bulk operations
  bulkUpdate(data) {
    return api.put(`${URL}/bulk_update`, data);
  }

  bulkDelete(data) {
    return api.delete(`${URL}/bulk_delete`, { data });
  }

  // Stock operations
  adjustStock(id, data) {
    return api.post(`${URL}/${id}/adjust_stock`, data);
  }

  // Search inventory items
  search(query) {
    return api.get(`${URL}/search?q=${encodeURIComponent(query)}`);
  }

  // Get inventory items by category
  getByCategory(categoryId) {
    return api.get(`${URL}/category/${categoryId}`);
  }

  // Get low stock items
  getLowStock() {
    return api.get(`${URL}/low_stock`);
  }

  // Get out of stock items
  getOutOfStock() {
    return api.get(URL, { params: { current_stock: 0, status: 'active', per_page: 5 } });
  }

  // Export inventory data
  export(format = 'csv') {
    return api.get(`${URL}/export?format=${format}`, {
      responseType: 'blob'
    });
  }

  // Import inventory data
  import(data) {
    return api.post(`${URL}/import`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  // Get low stock items with reorder suggestions
  getLowStockItems() {
    return api.get(`${URL}/low_stock`);
  }

  // Get inventory valuation report
  getInventoryValuation(method = 'WAC') {
    return api.get(`${URL}/valuation`, { params: { method } });
  }

  // Get inventory aging for specific item
  getInventoryAging(id) {
    return api.get(`${URL}/${id}/aging`);
  }

  // Reserve stock for orders
  reserveStock(id, data) {
    return api.post(`${URL}/${id}/reserve-stock`, data);
  }

  // Release reserved stock
  releaseReservedStock(id, data) {
    return api.post(`${URL}/${id}/release-reserved-stock`, data);
  }

  // Get stock status summary
  getStockStatusSummary() {
    return api.get(`${URL}/stock-status-summary`);
  }

  // Get items by stock status
  getItemsByStockStatus(status) {
    return api.get(`${URL}/by-stock-status/${status}`);
  }

  // Bulk update valuation method
  bulkUpdateValuationMethod(data) {
    return api.put(`${URL}/bulk_update_valuation_method`, data);
  }

  // Get ABC analysis
  getABCAnalysis() {
    return api.get(`${URL}/abc-analysis`);
  }

  // Get available consumption options for manual picking (serials + lot/expiry segments).
  // When editing, pass saleItemId so options include segments/serials already consumed for this invoice.
  getAvailableConsumptionOptions(inventoryItemId, saleItemId = null) {
    const params = saleItemId ? { sale_item_id: saleItemId } : {};
    return api.get(`${URL}/${inventoryItemId}/available_consumption_options`, { params });
  }

  // In-memory cache for tracking flags to avoid duplicate requests (e.g. same inv item in multiple rows).
  _trackingFlagsCache = {};

  // Get tracking flags only (track_serials, track_lots, track_expiry). Cached by inventory_item_id.
  getTrackingFlags(inventoryItemId) {
    if (!inventoryItemId) return Promise.resolve(null);
    if (this._trackingFlagsCache[inventoryItemId]) {
      return Promise.resolve(this._trackingFlagsCache[inventoryItemId]);
    }
    return api.get(`${URL}/${inventoryItemId}/tracking_flags`).then((res) => {
      const data = res?.data?.data;
      if (data && res?.data?.success) {
        this._trackingFlagsCache[inventoryItemId] = data;
        return data;
      }
      return null;
    }).catch(() => null);
  }
}

export default new InventoryItemService(); 
